import React, { useEffect, useRef, useState } from 'react';
import { Box, Menu, MenuItem, Divider, ListItemText, Typography } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TuneIcon from '@mui/icons-material/Tune';
import InsightsIcon from '@mui/icons-material/Insights';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { USE_CHART_DB, ADMIN_EMAILS } from '../config';

/**
 * Compact signed-in indicator with a sign-out menu.
 * Renders nothing when there's no session (AuthGate handles that path).
 */
export const UserMenu: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setSession(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (!session?.user?.email) return null;
  const email = session.user.email;

  const handleSignOut = async () => {
    setAnchorEl(null);
    await supabase.auth.signOut();
  };

  return (
    <>
      <Box
        component="button"
        ref={buttonRef}
        type="button"
        onClick={() => setAnchorEl(buttonRef.current)}
        aria-label="Account menu"
        sx={{
          background: 'none',
          border: 'none',
          padding: '4px 8px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          fontFamily: 'inherit',
          color: 'text.secondary',
          fontSize: '0.875rem',
          borderRadius: 1,
          '&:hover': { color: 'text.primary', backgroundColor: 'rgba(0,0,0,0.04)' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
        }}
      >
        <AccountCircleIcon fontSize="small" />
        <Box component="span" sx={{ color: 'text.primary' }}>{email}</Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Signed in as
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {email}
          </Typography>
        </Box>
        <Divider />
        {/* Render Dashboard: billing metrics over render history. Admin-only —
            the /api/admin routes 403 everyone else; this just hides the entry. */}
        {ADMIN_EMAILS.includes(email.toLowerCase()) && (
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/dashboard'); }}>
            <InsightsIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
            <ListItemText primary="Render dashboard" />
          </MenuItem>
        )}
        {/* Chart Settings: edit chart styles (templates, color, bilingual). Only
            in DB mode — in Sheets mode chart config lives in the Chart Config sheet. */}
        {USE_CHART_DB && (
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/chart-settings'); }}>
            <TuneIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
            <ListItemText primary="Chart settings" />
          </MenuItem>
        )}
        <MenuItem onClick={handleSignOut}>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>
    </>
  );
};
